import GridTile from "../grid/GridTile";
import Arc from "./Arc";
import ArcCache from "./ArcCache";
import ShadowCastCallback from "./ShadowCastCallback";
import ShadowCaster from "./ShadowCaster";

// See:
// https://github.com/ondras/rot.js/blob/master/src/fov/fov.ts
// https://github.com/ondras/rot.js/blob/master/src/fov/precise-shadowcasting.ts

/**
 * @extends {ShadowCaster}
 */
export default class PreciseShadowCaster extends ShadowCaster {
    /**
     * @param {WorldGrid} worldGrid 
     */
    constructor(worldGrid) {
        super(worldGrid);

        /**
         * @private
         * @type {Array.<Arc>}
         */
        this.shadows = [];

        /**
         * @private
         * @type {ArcCache}
         */
        this.cache = new ArcCache(2500);
    }
    
    /**
     * Cast shadows in the grid.
     * @override
     * @param {GridTile} tile     The center tile.
     * @param {number} tileRadius The radius of the light (in tiles, not pixels).
     * @param {ShadowCastCallback} callback The callback that will be invoked once for every tile that has light cast upon it.
     */
    castShadows(tile, tileRadius, callback) {
        // Super rare, but I think I've seen a null tile passed in before.
        if (tile == null) {
            return;
        }

        // this place is always visible
        callback.tileLighted(tile, 0, 1);

        const centerCol = tile.getTileCol();
        const centerRow = tile.getTileRow();

        //////this.shadows.clear();
        this.shadows.length = 0;
        this.cache.resetCache();

        // analyze surrounding cells in concentric rings, starting from the center
        for (let r = 1; r <= tileRadius; r++) {
            this.forEachCircleTile(centerCol, centerRow, r, callback);
        }
    }

    /**
     * @override
     * @param {GridTile} tile
     * @param {number} neighborIndex
     * @param {number} neighborCount
     * @param {number} radius
     * @param {ShadowCastCallback} callback The callback that will be invoked once for every tile that has light cast upon it.
     * @returns {boolean}
     */
    onCircleTile(tile, neighborIndex, neighborCount, radius, callback) {
        if (!tile) {
            return true;
        }

        // shift half-an-angle backwards to maintain consistency of 0-th cells
        const A1 = this.cache.getArc(neighborIndex > 0 ? 2 * neighborIndex - 1 : 2 * neighborCount - 1, 2 * neighborCount);
        const A2 = this.cache.getArc(2 * neighborIndex + 1, 2 * neighborCount);

        const blocks = tile.isOpaque();
        const visibility = this.checkVisibility(A1, A2, blocks, this.shadows);
        if (visibility > 0) {
            callback.tileLighted(tile, radius, visibility);
        }

        // Check for mysterious special case where we know we can stop processing circle tiles.
        if (this.shadows.length == 2 && this.shadows[0].a0 == 0 && this.shadows[1].a0 == this.shadows[1].a1) {
            return false;
        }

        return true;
    }

    /**
     * @private
     * @param {Arc} A1 
     * @param {Arc} A2 
     * @param {boolean} blocks 
     * @param {Array.<Arc>} shadows
     */
    checkVisibility(A1, A2, blocks, shadows) {
        if (A1.a0 > A2.a0) { /* split into two sub-arcs */
            const v1 = this.checkVisibility(A1, this.cache.getArc(A1.a1, A1.a1), blocks, shadows);
            const v2 = this.checkVisibility(this.cache.getArc(0, 1), A2, blocks, shadows);
            return (v1 + v2) / 2.0;
        }

        // index1: first shadow >= A1
        let index1 = 0;
        let edge1 = false;
        while (index1 < shadows.length) {
            const old = shadows[index1];
            const diff = old.a0 * A1.a1 - A1.a0 * old.a1;
            if (diff >= 0) { /* old >= A1 */
                if (diff == 0 && (index1 % 2 == 0)) {
                    edge1 = true;
                }
                break;
            }
            index1++;
        }

        // index2: last shadow <= A2
        let index2 = shadows.length;
        let edge2 = false;
        while (index2-- != 0) {
            const old = shadows[index2];
            const diff = A2.a0 * old.a1 - old.a0 * A2.a1;
            if (diff >= 0) { /* old <= A2 */
                if (diff == 0 && (index2 % 2 != 0)) {
                    edge2 = true;
                }
                break;
            }
        }

        let visible = true;
        if (index1 == index2 && (edge1 || edge2)) {
            // subset of existing shadow, one of the edges match
            visible = false;
        }
        else if (edge1 && edge2 && index1 + 1 == index2 && (index2 % 2 != 0)) {
            // completely equivalent with existing shadow
            visible = false;
        }
        else if (index1 > index2 && (index1 % 2 != 0)) {
            // subset of existing shadow, not touching
            visible = false;
        }

        // fast case: not visible
        if (!visible) {
            return 0;
        }

        let visibleLength;

        // compute the length of visible arc, adjust list of shadows (if blocking)
        const remove = index2 - index1 + 1;
        if (remove % 2 != 0) {
            if (index1 % 2 != 0) { /* first edge within existing shadow, second outside */
                const P = shadows[index1];
                const denominator = P.a1 * A2.a1;
                const numerator = A2.a0 * P.a1 - P.a0 * A2.a1;
                visibleLength = numerator / denominator;
                if (blocks) {
                    //this.spliceAddOne(shadows, index1, remove, A2);
                    shadows.splice(index1, remove, A2);
                }
            }
            else { /* second edge within existing shadow, first outside */
                const P = shadows[index2];
                const denominator = A1.a1 * P.a1;
                const numerator = P.a0 * A1.a1 - A1.a0 * P.a1;
                visibleLength = numerator / denominator;
                if (blocks) {
                    /////this.spliceAddOne(shadows, index1, remove, A1);
                    shadows.splice(index1, remove, A1);
                }
            }
        }
        else {
            if (index1 % 2 != 0) { /* both edges within existing shadows */
                const P1 = shadows[index1];
                const P2 = shadows[index2];
                const denominator = P1.a1 * P2.a1;
                const numerator = P2.a0 * P1.a1 - P1.a0 * P2.a1;
                visibleLength = numerator / denominator;
                if (blocks) {
                    //this.spliceOnly(shadows, index1, remove);
                    shadows.splice(index1, remove);
                }
            }
            else { /* both edges outside existing shadows */
                if (blocks) {
                    ////this.splice(shadows, index1, remove, A1, A2);
                    shadows.splice(index1, remove, A1, A2);
                }
                return 1; /* whole arc visible! */
            }
        }

        //////int arcLength = (A2.get(0) * A1.get(1) - A1.get(0) * A2.get(1)) / (A1.get(1) * A2.get(1));
        const denominator = A1.a1 * A2.a1;
        const numerator = A2.a0 * A1.a1 - A1.a0 * A2.a1;
        const arcLength = numerator / denominator;
        if (arcLength == 0) {
            return 1;
        }

        return visibleLength / arcLength;
    }

}